<template>
  <div class="order_list_container">
    <DxDataGrid
      :element-attr="gridAttributes"
      ref="orderGrid"
      class="order_grid"
      :data-source="dataSource"
      :no-data-text="$t('common.no_data_found')"
      :show-column-headers="false"
      :hover-state-enabled="true"
      :remote-operations="isRemoteOperation"
      :repaint-changes-only="true"
      :selected-row-keys="selectedOrders"
      @selection-changed="onGridSelectionChanged($event)"
      @row-click="onRowClick"
      height="100%"
      :columns="normalizedColumns"
    >
      <template #orderNumberWithPartnerCell="{ data }">
        <div class="order_number_partner_cell">
          <div class="order_number_line">{{ data?.data?.order_number || "-" }}</div>
          <div class="partner_name_line">{{ data?.data?.partner_name || "-" }}</div>
        </div>
      </template>
      <template #productWithRemainingCell="{ data }">
        <div class="product_remaining_cell">
          <div class="product_name_line">{{ data?.data?.product_name || "-" }}</div>
          <div class="remaining_quantity_line">
            {{ data?.data?.remaining_quantity ?? 0 }} {{ getMeasureUnitLabel(data?.data) }}
          </div>
        </div>
      </template>
      <DxSelection :mode="multiselect ? 'multiple' : 'single'" showCheckBoxesMode="never" selectAllMode="page"  />
      <DxFilterRow :visible="true" />
      <DxPaging :enabled="true" />
      <DxScrolling mode="infinite" />
      <DxGrouping :autoExpandAll="true" />
      <DxGroupPanel :visible="false" />
    </DxDataGrid>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import { DxDataGrid, DxSelection, DxFilterRow, DxScrolling, DxGrouping, DxGroupPanel, DxPaging } from "devextreme-vue/data-grid";
import { DxScrollView } from "devextreme-vue/scroll-view";
import DataSource from "devextreme/data/data_source";
import CustomStore from "devextreme/data/custom_store";

export default {
  name: "OrdersSelectorMobile",
  props: {
    localDataset: {
      type: Array,
      default: () => [],
    },
    dataColumns: {
      type: Array,
      default: () => [
        { dataField: "legal_entity_name", caption: "Entitate legală", groupIndex: "0" },
        { dataField: "order_number", caption: "Număr comandă" },
        { dataField: "product_name", caption: "Denumire produs" },
      ],
    },
    hasError: false,
    multiselect: {
      type: Boolean,
      default: () => false,
    },
    isRemoteOperation: {
      type: Boolean,
      default: () => true,
    },
    selectedOrders: {
      type: Array,
      default: () => [],
    },
    filterData: {
      type: Object,
      default: () => ({}),
    },
    toastMessageRef: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      gridAttributes: {
        class: "grid_general_mobile_style",
      },
    };
  },
  components: {
    DxScrollView,
    DxDataGrid,
    DxSelection,
    DxFilterRow,
    DxScrolling,
    DxGrouping,
    DxGroupPanel,
    DxPaging,
  },
  computed: {
    normalizedColumns() {
      return (this.dataColumns || [])
        .filter((column) => column?.dataField !== "remaining_quantity")
        .map((column) => {
        if (column?.dataField === "product_name") {
          return {
            ...column,
            cellTemplate: "productWithRemainingCell",
          };
        }

        if (column?.dataField !== "order_number") {
          return column;
        }

        return {
          ...column,
          cellTemplate: "orderNumberWithPartnerCell",
          calculateFilterExpression: this.orderNumberPartnerFilterExpression,
        };
        });
    },
    dataSource() {
      if (this.isRemoteOperation) {
        return this.customStore;
      }

      return this.localDataset;
    },
    customStore() {
      return new DataSource({
        store: new CustomStore({
          load: async (loadOptions) => {
            if (this.filterData && Object.keys(this.filterData).length > 0) {
              Object.assign(loadOptions, this.filterData);
            }

            const response = await this.getSmOrders(loadOptions);

            if (loadOptions.group && loadOptions.group.length && loadOptions.group[0].selector === "legal_entity_name") {
              const groups = [];
              const groupMap = {};

              response?.data?.data?.forEach((item) => {
                const key = item.legal_entity_name || "Fără entitate";
                if (!groupMap[key]) {
                  groupMap[key] = { key, items: [], count: 0 };
                  groups.push(groupMap[key]);
                }
                groupMap[key].items.push(item);
                groupMap[key].count++;
              });

              return {
                data: groups,
                totalCount: response?.data?.total || 0,
                summary: [],
                groupCount: groups.length,
              };
            }

            return {
              data: response?.data?.data || [],
              totalCount: response?.data?.total || 0,
            };
          },
          byKey: async function (key) {
            return new Promise((resolve) => resolve("success"));
          },
        }),
      });
    },
  },
  methods: {
    ...mapActions({
      getSmOrders: "smOrders/getSmOrders",
    }),
    onGridSelectionChanged(e) {
      this.$emit("update:selectedOrders", e.selectedRowKeys);
    },
    getMeasureUnitLabel(row) {
      const measureUnit = row?.measure_unit;
      if (measureUnit && typeof measureUnit === "object") {
        return measureUnit.symbol || measureUnit.name || "";
      }

      return measureUnit || row?.measure_unit_name || row?.measure_unit_symbol || "";
    },
    onRowClick(e) {
      if (!this.multiselect) {
        return;
      }

      if (e.rowType !== "data") {
        return;
      }

      const rowKey = e.key;
      const selectedIndex = this.selectedOrders.indexOf(rowKey);

      if (selectedIndex === -1) {
        const rowData = e.data;
        const newLegalEntityUuid = rowData.legal_entity_uuid;
        const newPartnerUuid = rowData.partner_uuid;
        const newProductUuid = rowData.product_uuid;

        if (this.selectedOrders.length > 0) {
          const allSameLegalEntity = this.selectedOrders.every((item) => item.legal_entity_uuid === newLegalEntityUuid);
          const allSamePartner = this.selectedOrders.every((item) => item.partner_uuid === newPartnerUuid);
          const productExists = this.selectedOrders.some((item) => item.product_uuid === newProductUuid && item.uuid !== rowData.uuid);

          if (!allSameLegalEntity) {
            if (this.toastMessageRef) {
              this.toastMessageRef.showError("Toate comenzile trebuie să aibă aceeași entitate legală.");
            }
            e.cancel = true;
            return;
          }
          if (!allSamePartner) {
            if (this.toastMessageRef) {
              this.toastMessageRef.showError("Toate comenzile trebuie să aibă același partener.");
            }
            e.cancel = true;
            return;
          }
          if (productExists) {
            if (this.toastMessageRef) {
              this.toastMessageRef.showError("Produsul selectat există deja în comenzile selectate.");
            }
            e.cancel = true;
            return;
          }
        }

        this.selectedOrders.push(rowKey);
      } else {
        this.selectedOrders.splice(selectedIndex, 1);
      }

      this.$refs.orderGrid.instance.selectRows(this.selectedOrders, false);
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/components/dxMobileGridGeneral.scss";

.order_list_container {
  width: 100%;
  height: 100%;
  padding-left: 10px;
  padding-right: 5px;
}

.order_list_container ::v-deep .dx-datagrid-rowsview {
  padding-bottom: 80px;
}

.order_list_container ::v-deep .dx-data-row > td {
  vertical-align: middle;
}

.order_number_partner_cell {
  display: flex;
  flex-direction: column;
  padding: 8px 0;
}

.order_number_line {
  font-weight: bold;
  font-size: 14px;
}

.partner_name_line {
  font-size: 12px;
  color: #666;
}

.order_type_line {
  font-size: 11px;
  color: #999;
  margin-top: 2px;
}

.product_remaining_cell {
  display: flex;
  flex-direction: column;
  line-height: 1.2;
}

.product_name_line {
  font-weight: 700;
}

.remaining_quantity_line {
  margin-top: 2px;
  color: #666;
  font-size: 12px;
}
</style>
